import React, { useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import { getNameInitials } from "../constants/globalFunctions";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { AvatarGroup } from "@mui/material";
import { fetchUserTagData } from "../../actions/setUserTagData";
import { RootState, type AppDispatch } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

export type AvatarSize = "XXS" | "XS" | "LSM" | "SM" | "LG" | "LLG" | "XL" | "GR";

const StyledAvatar = styled(Avatar, { shouldForwardProp: (prop) => prop !== "size" })<{ size?: AvatarSize }>(({
  size,
}) => {
  let fontSize = "1rem";
  if (size === "XXS" || size === "XS") {
    fontSize = "0.5rem";
  }

  return {
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    "&.MuiAvatar-root": {
      fontSize,
    },
  };
});

const getSizeClass = (size: AvatarSize = "SM") => {
  switch (size) {
    case "XXS":
      return {
        width: "1.25rem",
        height: "1.25rem",
      };
    case "XS":
      return {
        width: "1.5rem",
        height: "1.5rem",
      };
    case "LSM":
      return {
        width: "1.875rem",
        height: "1.875rem",
      };
    case "LG":
      return {
        width: "3rem",
        height: "3rem",
      };
    case "LLG":
      return {
        width: "4.6875rem",
        height: "4.6875rem",
      };
    case "XL":
      return {
        width: "6.25rem",
        height: "6.25rem",
      };
    case "GR":
      return {
        width: "2.5rem",
        height: "2.5rem",
      };
    case "SM":
    default:
      return {
        width: "2.25rem",
        height: "2.25rem",
      };
  }
};

export interface AvatarComponentProps {
  readonly isCircle?: boolean;
  readonly isSquare?: boolean;
  readonly size?: AvatarSize;
  readonly bgColor?: string;
  readonly style?: React.CSSProperties;
  readonly id?: string | number;
  readonly fullName?: string;
  readonly maxAvatars?: number;
  readonly groupedAvatars?: React.ReactNode;
  readonly userIdsForGroup?: Array<string | number>;
}

function AvatarComponent({
  isSquare = false,
  size = "SM",
  bgColor = "var(--system-colour-2)",
  style,
  id,
  fullName = "",
  maxAvatars,
  groupedAvatars,
  userIdsForGroup = [],
}: AvatarComponentProps) {
  const variant: "circular" | "square" = isSquare ? "square" : "circular";
  const dispatch = useDispatch<AppDispatch>();
  const userTagData = useSelector((state: RootState) => state.userTagData);

  useEffect(() => {
    if (id) {
      // `id` is this shared avatar's loose `string | number` prop (grid RowIds included), so the
      // account id enters the type system here, where it is normalized to a string.
      dispatch(fetchUserTagData(String(id) as ObjectIdString));
    }
  }, [id]);

  const avatarStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    ...getSizeClass(size),
    ...style,
  };

  // The `Any` suffix is now stale (the cast is gone); the identifier is kept because renaming a
  // local const perturbs the minifier's name assignment.
  const AvatarGroupAny = AvatarGroup;

  if (maxAvatars) {
    return (
      <AvatarGroupAny
        variant="circular"
        max={maxAvatars}
        renderSurplus={(surplus: number) => {
          if (surplus >= 1000) {
            return <span>{`${surplus.toString()[0]}k`}</span>;
          }
          return `+${surplus}`;
        }}
        slotProps={{
          additionalAvatar: {
            sx: {
              ...avatarStyle,
              fontSize: "0.75rem",
              border: "0.125rem solid #fff",
            },
          },
        }}
      >
        {groupedAvatars}
      </AvatarGroupAny>
    );
  }

  const userDataForId = id == null ? undefined : userTagData?.[id];

  const canShowUserData =
    userIdsForGroup.length === 0 && !maxAvatars && !!userDataForId?.data && !!userDataForId?.isLoaded && !!id;

  let avatarElement: React.ReactNode;

  if (canShowUserData) {
    // `canShowUserData` already asserts `userDataForId?.data`, but a boolean const does not narrow.
    if (userDataForId!.data!.profilePic) {
      avatarElement = (
        <StyledAvatar variant={variant} style={avatarStyle} size={size} src={userDataForId!.data!.profilePic} />
      );
    } else {
      avatarElement = (
        <StyledAvatar variant={variant} style={avatarStyle} size={size}>
          {getNameInitials({
            firstName: userDataForId!.data!.firstName,
            lastName: userDataForId!.data!.lastName,
          })}
        </StyledAvatar>
      );
    }
  } else {
    const firstName = fullName.split(" ")[0] || "";
    const lastName = fullName.split(" ")[1] || "";
    avatarElement = (
      <StyledAvatar variant={variant} style={avatarStyle} size={size}>
        {getNameInitials({
          firstName,
          lastName,
        })}
      </StyledAvatar>
    );
  }

  return <>{avatarElement}</>;
}

export default AvatarComponent;
